import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Upload,
  RefreshCw,
  Users,
  Zap,
  Target,
} from 'lucide-react';

const API_BASE = '/api/catapult';

const ACWR_STATUS = {
  OPTIMAL: { color: '#22c55e', label: 'Optimal' },
  HIGH_RISK: { color: '#ef4444', label: 'High Risk' },
  LOW: { color: '#eab308', label: 'Underloaded' },
  INSUFFICIENT_DATA: { color: '#52525b', label: 'No Data' },
};

const READINESS_STATUS = {
  READY: { dot: '#22c55e', text: 'Ready' },
  AT_RISK: { dot: '#ef4444', text: 'At Risk' },
  UNDERLOADED: { dot: '#eab308', text: 'Underloaded' },
  NO_DATA: { dot: '#52525b', text: 'No Data' },
};

// Compact SVG ACWR ring gauge
const ACWRGauge = ({ value, status }) => {
  const pct = value ? Math.min(value / 2, 1) : 0; // ACWR 2.0 = 100%
  const statusInfo = ACWR_STATUS[status] || ACWR_STATUS.INSUFFICIENT_DATA;
  const r = 32;
  const circ = 2 * Math.PI * r;
  const dash = pct * circ;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} stroke="#1F1F1F" strokeWidth="8" fill="none" />
        <circle
          cx="40" cy="40" r={r}
          stroke={statusInfo.color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
        />
        <text x="40" y="37" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">
          {value?.toFixed(2) || '--'}
        </text>
        <text x="40" y="49" textAnchor="middle" fill="#71717a" fontSize="8">
          ACWR
        </text>
      </svg>
      <span
        className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
        style={{ color: statusInfo.color, border: `1px solid ${statusInfo.color}40` }}
      >
        {statusInfo.label}
      </span>
    </div>
  );
};

export default function CatapultDashboard() {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [teamReadiness, setTeamReadiness] = useState(null);
  const [athleteData, setAthleteData] = useState(null);
  const [loadTrend, setLoadTrend] = useState(null);
  const [deficiencies, setDeficiencies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);

  const teams = [
    { id: 'team-1', name: 'Duke Blue Devils' },
    { id: 'team-2', name: 'Kentucky Wildcats' },
  ];

  const fetchTeamReadiness = useCallback(async (teamId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/team/${teamId}/readiness`);
      const data = await response.json();
      if (data.success) {
        setTeamReadiness(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoadTrend = useCallback(async (athleteId) => {
    try {
      const response = await fetch(`${API_BASE}/athlete/${athleteId}/load-trend?weeks=4`);
      const data = await response.json();
      if (data.success) setLoadTrend(data.data);
    } catch (err) {
      console.error('Error fetching load trend:', err);
    }
  }, []);

  const fetchDeficiencies = useCallback(async (athleteId) => {
    try {
      const response = await fetch(`${API_BASE}/athlete/${athleteId}/deficiencies`);
      const data = await response.json();
      if (data.success) setDeficiencies(data.data);
    } catch (err) {
      console.error('Error fetching deficiencies:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedTeam) fetchTeamReadiness(selectedTeam);
  }, [selectedTeam, fetchTeamReadiness]);

  useEffect(() => {
    if (selectedAthlete) {
      fetchLoadTrend(selectedAthlete);
      fetchDeficiencies(selectedAthlete);
    }
  }, [selectedAthlete, fetchLoadTrend, fetchDeficiencies]);

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedAthlete) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('athleteId', selectedAthlete);
    try {
      const response = await fetch(`${API_BASE}/import`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.success) {
        fetchLoadTrend(selectedAthlete);
        fetchDeficiencies(selectedAthlete);
      }
    } catch (err) {
      console.error('Import error:', err);
    } finally {
      setImporting(false);
    }
  };

  const summaryStats = teamReadiness?.summary ? [
    { label: 'Athletes', value: teamReadiness.summary.totalAthletes, icon: Users, color: 'text-amber-400' },
    { label: 'Ready', value: teamReadiness.summary.ready, icon: CheckCircle, color: 'text-green-400' },
    { label: 'At Risk', value: teamReadiness.summary.atRisk, icon: AlertTriangle, color: 'text-red-400' },
    { label: 'Underloaded', value: teamReadiness.summary.underloaded, icon: TrendingDown, color: 'text-yellow-400' },
  ] : [];

  return (
    <div className="min-h-full bg-black text-white p-3 pb-16">

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-amber-400" />
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Catapult</p>
            <h1 className="text-[15px] font-semibold tracking-tight">Load Management</h1>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={selectedTeam || ''}
            onChange={(e) => setSelectedTeam(e.target.value || null)}
            className="px-2 py-1.5 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[11px] text-white focus:outline-none focus:border-amber-400"
          >
            <option value="">Select team</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          <label className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-xl border text-[11px] cursor-pointer transition-colors ${
            selectedAthlete
              ? 'border-amber-400/50 bg-amber-500/15 text-amber-200'
              : 'border-[#1F1F1F] text-zinc-600 opacity-50 cursor-not-allowed'
          }`}>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileImport}
              className="hidden"
              disabled={!selectedAthlete || importing}
            />
            {importing ? <RefreshCw size={11} className="animate-spin" /> : <Upload size={11} />}
            CSV
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 mb-3">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-5 h-5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!selectedTeam && !loading && (
        <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-10 text-center">
          <Activity size={28} className="mx-auto mb-2 text-zinc-700" />
          <p className="text-[12px] text-zinc-400 mb-1">Select a team to get started</p>
          <p className="text-[10px] text-zinc-600">View athlete readiness and load data</p>
        </div>
      )}

      {teamReadiness && !loading && (
        <div className="space-y-3">
          {/* Summary stat grid */}
          <div className="grid grid-cols-4 gap-1.5">
            {summaryStats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] px-2 py-2.5 text-center">
                  <Icon size={12} className={`${stat.color} mx-auto mb-1`} />
                  <div className="text-[16px] font-bold leading-none">{stat.value}</div>
                  <div className="text-[8px] text-zinc-600 mt-0.5 uppercase tracking-widest">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Athlete grid */}
          <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A]">
            <div className="px-4 py-2.5 border-b border-[#1F1F1F]">
              <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 flex items-center gap-1.5">
                <Users size={10} />
                Team Readiness
              </p>
            </div>
            <div className="p-2.5 grid grid-cols-3 gap-1.5">
              {teamReadiness.athletes?.map((athlete) => {
                const statusInfo = READINESS_STATUS[athlete.status] || READINESS_STATUS.NO_DATA;
                const isSelected = selectedAthlete === athlete.athleteId;
                return (
                  <button
                    key={athlete.athleteId}
                    onClick={() => setSelectedAthlete(athlete.athleteId)}
                    className={`px-2 py-2 rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-amber-400/40 bg-amber-500/10'
                        : 'border-[#1F1F1F] bg-white/[0.02]'
                    }`}
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusInfo.dot }} />
                      <p className="text-[10px] text-zinc-200 truncate">{athlete.name}</p>
                    </div>
                    <p className="text-[8px] text-zinc-600">{athlete.position}</p>
                    {athlete.acwrLoad && (
                      <p className="text-[9px] text-zinc-500 mt-0.5">ACWR {athlete.acwrLoad.toFixed(2)}</p>
                    )}
                  </button>
                );
              })}
              {(!teamReadiness.athletes || teamReadiness.athletes.length === 0) && (
                <div className="col-span-3 text-center py-6 text-[11px] text-zinc-600">No athletes found</div>
              )}
            </div>
          </div>

          {/* Individual athlete panel */}
          {selectedAthlete && (
            <>
              {/* Load trend + ACWR */}
              <div className="grid grid-cols-3 gap-2">
                {/* 4-Week load bar chart (2/3 width) */}
                <div className="col-span-2 rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-2 flex items-center gap-1">
                    <TrendingUp size={9} />
                    4-Week Load Trend
                  </p>
                  {loadTrend?.weeklyData ? (
                    <div className="flex items-end gap-1.5 h-20">
                      {loadTrend.weeklyData.map((week, i) => {
                        const maxLoad = Math.max(...loadTrend.weeklyData.map((w) => w.totalLoad || 0), 1);
                        const pct = ((week.totalLoad || 0) / maxLoad) * 100;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                            <div
                              className="w-full rounded-t-sm"
                              style={{ height: `${Math.max(pct, 4)}%`, backgroundColor: '#a855f7', opacity: 0.8 }}
                            />
                            <span className="text-[7px] text-zinc-600">W{week.week}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-20 flex items-center justify-center">
                      <p className="text-[10px] text-zinc-600">Import CSV to view trends</p>
                    </div>
                  )}
                </div>

                {/* ACWR gauge (1/3 width) */}
                <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5 flex flex-col items-center justify-center gap-1">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Workload</p>
                  <ACWRGauge
                    value={loadTrend?.acwr?.acwrLoad}
                    status={loadTrend?.acwr?.status || 'INSUFFICIENT_DATA'}
                  />
                </div>
              </div>

              {/* Deficiency analysis */}
              {deficiencies && (
                <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A]">
                  <div className="px-4 py-2.5 border-b border-[#1F1F1F]">
                    <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 flex items-center gap-1.5">
                      <Target size={10} />
                      Performance Gaps
                      {deficiencies.athlete?.position && (
                        <span className="text-zinc-700">· {deficiencies.athlete.position}</span>
                      )}
                    </p>
                  </div>
                  <div className="px-4 py-3">
                    {deficiencies.analysis?.deficiencies?.length > 0 ? (
                      <div className="space-y-3">
                        {deficiencies.analysis.deficiencies.map((d, i) => {
                          const pct = Math.min((d.currentValue / d.benchmark) * 100, 100);
                          const barColor = d.severity === 'HIGH' ? '#ef4444' : d.severity === 'MEDIUM' ? '#eab308' : '#71717a';
                          return (
                            <div key={i} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-zinc-300">{d.area}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-zinc-500">
                                    {d.currentValue?.toFixed(1)} / {d.benchmark}
                                  </span>
                                  <span
                                    className="text-[9px] px-1.5 py-0.5 rounded-full"
                                    style={{ color: barColor, border: `1px solid ${barColor}40` }}
                                  >
                                    -{d.gap}%
                                  </span>
                                </div>
                              </div>
                              <div className="h-[3px] rounded-full bg-[#1A1A1A] overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{ width: `${pct}%`, backgroundColor: barColor }}
                                />
                              </div>
                            </div>
                          );
                        })}

                        {deficiencies.analysis.trainingPriorities?.length > 0 && (
                          <div className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/5 px-3 py-2">
                            <p className="text-[9px] uppercase tracking-[0.18em] text-amber-400 mb-1.5 flex items-center gap-1">
                              <Zap size={9} />
                              Training Priorities
                            </p>
                            <ul className="space-y-1">
                              {deficiencies.analysis.trainingPriorities.map((p, i) => (
                                <li key={i} className="text-[10px] text-zinc-400 flex items-start gap-1.5">
                                  <span className="text-amber-400 mt-0.5">·</span>
                                  {p}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <CheckCircle size={22} className="mx-auto mb-1.5 text-green-400" />
                        <p className="text-[11px] text-zinc-300">No significant deficiencies</p>
                        <p className="text-[10px] text-zinc-600">Meeting or exceeding benchmarks</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
